import React from 'react'
import ClientDataService from '../../services/ClientData.service'
import ReportService from '../../services/reports/Report.service';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import EpochMonthService from '../../services/EpochMonth.service'
import MonthSelector from '../../tailwindComponents/ui/dates/month-selector.component';
import { RawData, TableData } from '../../interfaces/dashboards/KpiDashboard.interface';
import MultiSelect from '../../tailwindComponents/ui/multi-select';

const TODAY_EPOCH = EpochMonthService.getTodayEpochMonth();

const HIERARCHY_OPTIONS: Record<string, string[]> = {
    "Company Total": [],
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
};

function hierarchyUsesChildren(hierarchy: string[]): boolean {
    return hierarchy.includes('parentClientClassCode');
}

function normalizeItemForHierarchy(item: RawData, hierarchy: string[]): RawData {
    if (hierarchyUsesChildren(hierarchy) || !item.parentClientClassCode) {
        return item;
    }

    return {
        ...item,
        classCode: item.parentClientClassCode,
        className: item.parentClientClassName || item.className,
    };
}

function hierarchyKeyForItem(item: RawData, hierarchy: string[]): string {
    if (hierarchy.length === 0) return 'Company';
    return hierarchy.map(h => {
        switch (h) {
            case 'locationCode': return `${item.locationName ? `${item.locationCode} ${item.locationName}` : item.locationCode}`;
            case 'classCode': return `${item.className ? `${item.classCode} ${item.className}` : item.classCode}`;
            case 'category': return item.category || 'N/A';
            case 'parentClientClassCode':
                return item.parentClientClassCode
                    ? `${item.parentClientClassCode} ${item.parentClientClassName || item.className}`
                    : `${item.classCode} ${item.className}`;
            default: return h;
        }
    }).join(' / ');
}

function parseTableData({ rawData, hierarchy }: { rawData: RawData[], hierarchy: string[] }): TableData[] {
    // Group data
    const groupedData: { [key: string]: any } = {};
    rawData.forEach(item => {
        const displayKey = hierarchyKeyForItem(item, hierarchy);

        if (!groupedData[displayKey]) {
            groupedData[displayKey] = {
                salesPlan: 0,
                sales: 0,
                salesComp: 0,
                markdownsPlan: 0,
                markdowns: 0,
                markdownsComp: 0,
                mardownPctPlan: 0,
                markdownPct: 0,
                markdownPctComp: 0,
                imuPlan: 0,
                imu: 0,
                imuComp: 0,
                mmuPlan: 0,
                mmu: 0,
                mmuComp: 0,
                cashMarginPlan: 0,
                cashMargin: 0,
                cashMarginComp: 0,
                cashMarginPctPlan: 0,
                cashMarginPct: 0,
                cashMarginPctComp: 0,
                inflowPlanCost: 0,
                inflowCost: 0,
                inflowCostComp: 0,
                inflowPlanRetail: 0,
                inflowRetail: 0,
                inflowRetailComp: 0,
                costOfPurchasesPlanPct: 0,
                costOfPurchasesPct: 0,
                costOfPurchasesPctComp: 0,
                turnPlan: 0,
                turn: 0,
                turnComp: 0,
                efficiencyPlan: 0,
                efficiency: 0,
                efficiencyComp: 0,
                cmroiPlan: 0,
                cmroi: 0,
                cmroiComp: 0,
                gmroiPlan: 0,
                gmroi: 0,
                gmroiComp: 0,
                
                // hidden items
                inventoryAvg: 0,
                inventoryAvgPlan: 0,
                inventoryCurrent: 0,
                rcrAcrossLocations: 0,
                rccAcrossLocations: 0,
                rcrAcrossLocationsComp: 0,
                rccAcrossLocationsComp: 0,
            }
        }
        const row = groupedData[displayKey];
        // Sum all numeric fields
        row.salesPlan += item.salesPlan;
        row.sales += item.sales;
        row.salesComp += item.salesComp || 0;
        row.markdownsPlan += item.markdownsPlan;
        row.markdowns += item.markdowns;
        row.markdownsComp += item.markdownsComp || 0;
        row.inflowPlanCost += item.inflowPlanCost;
        row.inflowCost += item.inflowCost;
        row.inflowCostComp += item.inflowCostComp || 0;
        row.inflowPlanRetail += item.inflowPlanRetail;
        row.inflowRetail += item.inflowRetail;
        row.inflowRetailComp += item.inflowRetailComp || 0;
        row.inventoryCurrent += item.inventoryCurrent;
        row.inventoryAvg += item.inventoryBomAvg;
        row.inventoryAvgPlan += item.inventoryBomPlanAvg;
        row.inventoryCurrentComp += item.inventoryCurrentComp || 0;
        row.inventoryAvgComp += item.inventoryAvgComp || 0;
        row.rcrAcrossLocations += item.rcrAcrossLocations || 0;
        row.rccAcrossLocations += item.rccAcrossLocations || 0;
        row.rcrAcrossLocationsComp += item.rcrAcrossLocationsComp || 0;
        row.rccAcrossLocationsComp += item.rccAcrossLocationsComp || 0;
    });

    // Now calculate percent/ratio fields for each group
    const tableData: TableData[] = Object.values(groupedData).map((row: any) => {
        // Calculate percent/ratio fields using ReportService
        // IMU
        row.imu = ReportService.calculateImu({ receivedRetailAcrossLocations: row.rcrAcrossLocations, receivedCostAcrossLocations: row.rccAcrossLocations });
        row.imuPlan = ReportService.calculateImu({ receivedRetailAcrossLocations: row.inflowPlanRetail, receivedCostAcrossLocations: row.inflowPlanCost });
        row.imuComp = ReportService.calculateImu({ receivedRetailAcrossLocations: row.rcrAcrossLocationsComp, receivedCostAcrossLocations: row.rccAcrossLocationsComp });

        const costComplement = 1 - row.imu;
        const costComplementPlan = 1 - row.imuPlan;
        const costComplementComp = 1 - row.imuComp;

        // MMU (using markdownPct, which is markdowns/sales)
        const markdownPct = row.sales ? row.markdowns / row.sales : 0;
        const markdownPctPlan = row.salesPlan ? row.markdownsPlan / row.salesPlan : 0;
        const markdownPctComp = row.salesComp ? row.markdownsComp / row.salesComp : 0;
        row.markdownPct = markdownPct;
        row.mardownPctPlan = markdownPctPlan;
        row.markdownPctComp = markdownPctComp;
        row.mmu = ReportService.calculateMmu({ receivedRetailAcrossLocations: row.rcrAcrossLocations, receivedCostAcrossLocations: row.rccAcrossLocations, markdownPercent: markdownPct });
        row.mmuPlan = ReportService.calculateMmu({ receivedRetailAcrossLocations: row.inflowPlanRetail, receivedCostAcrossLocations: row.inflowPlanCost, markdownPercent: markdownPctPlan });
        row.mmuComp = ReportService.calculateMmu({ receivedRetailAcrossLocations: row.rcrAcrossLocationsComp, receivedCostAcrossLocations: row.rccAcrossLocationsComp, markdownPercent: markdownPctComp });
        // Cash Margin
        row.cashMargin = ReportService.calculateCashMargin({ sales: row.sales, inflowCost: row.inflowCost, format: false });
        row.cashMarginPlan = ReportService.calculateCashMargin({ sales: row.salesPlan, inflowCost: row.inflowPlanCost, format: false });
        row.cashMarginComp = ReportService.calculateCashMargin({ sales: row.salesComp, inflowCost: row.inflowCostComp, format: false });
        // Cost of Purchases %
        row.costOfPurchasesPct = ReportService.calculateCop({ inflowCost: row.inflowCost, sales: row.sales });
        row.costOfPurchasesPlanPct = ReportService.calculateCop({ inflowCost: row.inflowPlanCost, sales: row.salesPlan });
        row.costOfPurchasesPctComp = ReportService.calculateCop({ inflowCost: row.inflowCostComp, sales: row.salesComp });
        // Turn
        row.turn = ReportService.calculateTurn({ sales: row.sales, invAvg: row.inventoryAvg, format: false });
        row.turnPlan = ReportService.calculateTurn({ sales: row.salesPlan, invAvg: row.inventoryAvgPlan, format: false });
        row.turnComp = ReportService.calculateTurn({ sales: row.salesComp, invAvg: row.inventoryAvg, format: false });
        // Efficiency
        row.efficiency = ReportService.calculateEfficiency({ sales: row.sales, inventory: row.inventoryCurrent, inflowRetail: row.inflowRetail, format: false });
        row.efficiencyPlan = ReportService.calculateEfficiency({ sales: row.salesPlan, inventory: row.inventoryCurrent, inflowRetail: row.inflowPlanRetail, format: false });
        row.efficiencyComp = ReportService.calculateEfficiency({ sales: row.salesComp, inventory: row.inventoryCurrent, inflowRetail: row.inflowRetailComp, format: false });
        // CMROI, GMROI
        row.cmroi = ReportService.calculateCmroi({ sales: row.sales, inflowCost: row.inflowCost, invAvgCost: row.inventoryAvg * costComplement, format: false });
        row.gmroi = ReportService.calculateGmroi({ sales: row.sales, markdowns: row.markdowns, costComplement: costComplement, imu: row.imu, invAvgCost: row.inventoryAvg * costComplement, format: false });
        row.cmroiPlan = ReportService.calculateCmroi({ sales: row.salesPlan, inflowCost: row.inflowPlanCost, invAvgCost: row.inventoryAvgPlan * costComplementPlan, format: false });
        row.gmroiPlan = ReportService.calculateGmroi({ sales: row.salesPlan, markdowns: row.markdownsPlan, costComplement: costComplementPlan, imu: row.imuPlan, invAvgCost: row.inventoryAvgPlan * costComplementPlan, format: false });
        row.cmroiComp = ReportService.calculateCmroi({ sales: row.salesComp, inflowCost: row.inflowCostComp, invAvgCost: row.inventoryAvg * costComplementComp, format: false });
        row.gmroiComp = ReportService.calculateGmroi({ sales: row.salesComp, markdowns: row.markdownsComp, costComplement: costComplementComp, imu: row.imuComp, invAvgCost: row.inventoryAvg * costComplementComp, format: false });
        // Format all fields as strings for TableData
        return {
            salesPlan: ReportService.formatCurrency(row.salesPlan),
            sales: ReportService.formatCurrency(row.sales),
            salesComp: ReportService.formatCurrency(row.salesComp),
            markdownsPlan: ReportService.formatCurrency(row.markdownsPlan),
            markdowns: ReportService.formatCurrency(row.markdowns),
            markdownsComp: ReportService.formatCurrency(row.markdownsComp),
            mardownPctPlan: ReportService.formatPercent(row.mardownPctPlan * 100),
            markdownPct: ReportService.formatPercent(row.markdownPct * 100),
            markdownPctComp: ReportService.formatPercent(row.markdownPctComp * 100),
            imuPlan: ReportService.formatPercent(row.imuPlan * 100),
            imu: ReportService.formatPercent(row.imu * 100),
            imuComp: ReportService.formatPercent(row.imuComp * 100),
            mmuPlan: ReportService.formatPercent(row.mmuPlan * 100),
            mmu: ReportService.formatPercent(row.mmu * 100),
            mmuComp: ReportService.formatPercent(row.mmuComp * 100),
            cashMarginPlan: ReportService.formatCurrency(row.cashMarginPlan),
            cashMargin: ReportService.formatCurrency(row.cashMargin),
            cashMarginComp: ReportService.formatCurrency(row.cashMarginComp),
            cashMarginPctPlan: ReportService.formatPercent(row.salesPlan ? row.cashMarginPlan / row.salesPlan * 100 : 0),
            cashMarginPct: ReportService.formatPercent(row.sales ? row.cashMargin / row.sales * 100 : 0),
            cashMarginPctComp: ReportService.formatPercent(row.salesComp ? row.cashMarginComp / row.salesComp * 100 : 0),
            inflowPlanCost: ReportService.formatCurrency(row.inflowPlanCost),
            inflowCost: ReportService.formatCurrency(row.inflowCost),
            inflowCostComp: ReportService.formatCurrency(row.inflowCostComp),
            inflowPlanRetail: ReportService.formatCurrency(row.inflowPlanRetail),
            inflowRetail: ReportService.formatCurrency(row.inflowRetail),
            inflowRetailComp: ReportService.formatCurrency(row.inflowRetailComp),
            costOfPurchasesPlanPct: ReportService.formatPercent(row.costOfPurchasesPlanPct * 100),
            costOfPurchasesPct: ReportService.formatPercent(row.costOfPurchasesPct * 100),
            costOfPurchasesPctComp: ReportService.formatPercent(row.costOfPurchasesPctComp * 100),
            turnPlan: typeof row.turnPlan === 'number' ? row.turnPlan.toFixed(1) : '',
            turn: typeof row.turn === 'number' ? row.turn.toFixed(1) : '',
            turnComp: typeof row.turnComp === 'number' ? row.turnComp.toFixed(1) : '',
            efficiencyPlan: typeof row.efficiencyPlan === 'number' ? row.efficiencyPlan.toFixed(1) + '%' : '',
            efficiency: typeof row.efficiency === 'number' ? row.efficiency.toFixed(1) + '%' : '',
            efficiencyComp: typeof row.efficiencyComp === 'number' ? row.efficiencyComp.toFixed(1) + '%' : '',
            cmroiPlan: typeof row.cmroiPlan === 'number' ? row.cmroiPlan.toFixed(1) : '',
            cmroi: typeof row.cmroi === 'number' ? row.cmroi.toFixed(1) : '',
            cmroiComp: typeof row.cmroiComp === 'number' ? row.cmroiComp.toFixed(1) : '',
            gmroiPlan: typeof row.gmroiPlan === 'number' ? row.gmroiPlan.toFixed(1) : '',
            gmroi: typeof row.gmroi === 'number' ? row.gmroi.toFixed(1) : '',
            gmroiComp: typeof row.gmroiComp === 'number' ? row.gmroiComp.toFixed(1) : '',
        };
    });

    return tableData;
}

type GroupEntry = {
    key: string;
    displayLabel: string;
    data: TableData;
    depth: number;     // 0=company total, 1=first-level subtotal, N=leaf
    parentKey?: string;
};

function buildHierarchicalGroups(filteredRawData: RawData[], hierarchy: string[]): GroupEntry[] {
    if (filteredRawData.length === 0) return [];

    function parseLevelData(levelHierarchy: string[], data: RawData[]): Map<string, TableData> {
        const parsed = parseTableData({ rawData: data, hierarchy: levelHierarchy });
        const keyOrder: string[] = [];
        const seen = new Set<string>();
        for (const item of data) {
            const k = levelHierarchy.length === 0 ? 'Company Total' : hierarchyKeyForItem(item, levelHierarchy);
            if (!seen.has(k)) { seen.add(k); keyOrder.push(k); }
        }
        const map = new Map<string, TableData>();
        keyOrder.forEach((k, idx) => { if (parsed[idx]) map.set(k, parsed[idx]); });
        return map;
    }

    if (hierarchy.length === 0) {
        const map = parseLevelData([], filteredRawData);
        const data = map.get('Company Total');
        return data ? [{ key: 'Company Total', displayLabel: 'Company Total', data, depth: 0 }] : [];
    }

    const leafDataMap = parseLevelData(hierarchy, filteredRawData);

    if (hierarchy.length === 1) {
        const sortedKeys = Array.from(leafDataMap.keys()).sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
        return sortedKeys.map(k => ({ key: k, displayLabel: k, data: leafDataMap.get(k)!, depth: 1 }));
    }

    // Multi-level (N levels): company total + intermediate subtotals + leaves
    // Build intermediate level maps: intermediateMaps[i] = parseLevelData(hierarchy.slice(0, i+1))
    const intermediateMaps: Map<string, TableData>[] = [];
    for (let l = 1; l < hierarchy.length; l++) {
        intermediateMaps.push(parseLevelData(hierarchy.slice(0, l), filteredRawData));
    }

    // Build parent -> children sets for each level transition
    // hierarchyTree[l] maps key-at-level-l to Set<key-at-level-l+1>
    const hierarchyTree: Map<string, Set<string>>[] = Array.from({ length: hierarchy.length }, () => new Map());
    for (const item of filteredRawData) {
        for (let l = 0; l < hierarchy.length; l++) {
            const parentKey = l === 0 ? 'Company Total' : hierarchyKeyForItem(item, hierarchy.slice(0, l));
            const childKey = hierarchyKeyForItem(item, hierarchy.slice(0, l + 1));
            if (!hierarchyTree[l].has(parentKey)) hierarchyTree[l].set(parentKey, new Set());
            hierarchyTree[l].get(parentKey)!.add(childKey);
        }
    }

    const companyDataMap = parseLevelData([], filteredRawData);
    const result: GroupEntry[] = [];
    const companyData = companyDataMap.get('Company Total');
    if (companyData) result.push({ key: 'Company Total', displayLabel: 'Company Total', data: companyData, depth: 0 });

    function addChildren(parentKey: string, levelIndex: number, depth: number) {
        const children = Array.from(hierarchyTree[levelIndex]?.get(parentKey) || []).sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
        for (const childKey of children) {
            const isLeaf = levelIndex === hierarchy.length - 1;
            const data = isLeaf ? leafDataMap.get(childKey) : intermediateMaps[levelIndex]?.get(childKey);
            if (!data) continue;
            const parts = childKey.split(' / ');
            const displayLabel = depth === 1 ? childKey : parts[parts.length - 1];
            result.push({
                key: childKey,
                displayLabel,
                data,
                depth,
                parentKey: parentKey === 'Company Total' ? undefined : parentKey,
            });
            if (!isLeaf) {
                addChildren(childKey, levelIndex + 1, depth + 1);
            }
        }
    }

    addChildren('Company Total', 0, 1);

    return result;
}

export default function KpiTable({ clientId }: { clientId: number | string }) {
    const [endEpochMonth, setEndEpochMonth] = React.useState<number>(TODAY_EPOCH);
    const [startEpochMonth, setStartEpochMonth] = React.useState<number>(TODAY_EPOCH - 11);
    const [hierarchy, setHierarchy] = React.useState<string[]>(["locationCode"]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [compareToYearsAgo, setCompareToYearsAgo] = React.useState<number>(1);
    const [swapDimensions, setSwapDimensions] = React.useState<boolean>(false);

    const [rawVersion, setRawVersion] = React.useState<number>(0);

    const [locationFilter, setLocationFilter] = React.useState<string[]>(['ALL']);
    const [classFilter, setClassFilter] = React.useState<string[]>(['ALL']);
    const [categoryFilter, setCategoryFilter] = React.useState<string[]>(['ALL']);

    // Ensure filters always have the default ALL state (specifically handles cases where the UI returns [])
    React.useEffect(() => {
        if (!locationFilter || locationFilter.length === 0) setLocationFilter(['ALL']);
    }, [locationFilter]);
    React.useEffect(() => {
        if (!classFilter || classFilter.length === 0) setClassFilter(['ALL']);
    }, [classFilter]);
    React.useEffect(() => {
        if (!categoryFilter || categoryFilter.length === 0) setCategoryFilter(['ALL']);
    }, [categoryFilter]);

    const rawDataRef = React.useRef<RawData[]>([]);
    const [allEntries, setAllEntries] = React.useState<GroupEntry[]>([]);

    const displayRawData = React.useMemo(
        () => (rawDataRef.current || []).map(item => normalizeItemForHierarchy(item, hierarchy)),
        [rawVersion, hierarchy]
    );

    const locationOptions = React.useMemo(() => {
        const map = new Map<string, string>();
        displayRawData.forEach((r) => {
            const code = String(r.locationCode ?? '').trim();
            if (!code) return;
            const name = String(r.locationName ?? '').trim();
            map.set(code, name ? `${code} ${name}` : code);
        });
        const codes = Array.from(map.keys()).sort((a, b) => {
            const na = Number(a);
            const nb = Number(b);
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            return a.localeCompare(b);
        });
        return codes.map(code => ({ code, label: map.get(code) || code }));
    }, [displayRawData]);

    const categoryOptions = React.useMemo(() => {
        const set = new Set<string>();
        displayRawData.forEach((r) => set.add((r.category || 'N/A') as string));
        return Array.from(set)
            .filter(Boolean)
            .sort((a, b) => (a || '').localeCompare(b || ''))
            .map(c => ({ code: c, label: c }));
    }, [displayRawData]);

    const classOptions = React.useMemo(() => {
        const map = new Map<string, string>();
        displayRawData.forEach((r) => {
            const code = String(r.classCode ?? '').trim();
            if (!code) return;
            const name = String(r.className ?? '').trim();
            map.set(code, name ? `${code} ${name}` : code);
        });
        const codes = Array.from(map.keys()).sort((a, b) => {
            const na = Number(a);
            const nb = Number(b);
            if (!isNaN(na) && !isNaN(nb)) return na - nb;
            return a.localeCompare(b);
        });
        return codes.map(code => ({ code, label: map.get(code) || code }));
    }, [displayRawData]);

    React.useEffect(() => {
        if (hierarchyUsesChildren(hierarchy) || !classFilter || classFilter.length === 0 || classFilter.includes('ALL')) {
            return;
        }

        const normalizedFilter = Array.from(new Set(
            classFilter.map((selectedCode) => {
                const selectedItem = (rawDataRef.current || []).find((item) => String(item.classCode ?? '').trim() === selectedCode);
                return selectedItem?.parentClientClassCode || selectedCode;
            })
        ));

        const changed = normalizedFilter.length !== classFilter.length
            || normalizedFilter.some((value, index) => value !== classFilter[index]);

        if (changed) {
            setClassFilter(normalizedFilter.length ? normalizedFilter : ['ALL']);
        }
    }, [hierarchy, classFilter, rawVersion]);

    const filteredRawData = React.useMemo(() => {
        const locSet = new Set((Array.isArray(locationFilter) && locationFilter.includes('ALL')) ? [] : (locationFilter || []));
        const clsSet = new Set((Array.isArray(classFilter) && classFilter.includes('ALL')) ? [] : (classFilter || []));
        const catSet = new Set((Array.isArray(categoryFilter) && categoryFilter.includes('ALL')) ? [] : (categoryFilter || []));

        return displayRawData.filter((r) => {
            const locOk = locSet.size === 0 || locSet.has(String(r.locationCode ?? '').trim());
            const clsOk = clsSet.size === 0 || clsSet.has(String(r.classCode ?? '').trim());
            const catOk = catSet.size === 0 || catSet.has((r.category || 'N/A') as string);
            return locOk && clsOk && catOk;
        });
    }, [displayRawData, locationFilter, classFilter, categoryFilter]);

    React.useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError(null);
            try {
                const response = await ClientDataService.getClientKpiData({
                    clientId,
                    startEpochMonth,
                    endEpochMonth,
                    compareToYearsAgo,
                });

                const data: RawData[] = response?.data || [];
                rawDataRef.current = data;
                setRawVersion(v => v + 1);
            } catch (err: any) {
                setError('Failed to load data');
                rawDataRef.current = [];
                setRawVersion(v => v + 1);
                setAllEntries([]);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [startEpochMonth, endEpochMonth, compareToYearsAgo, clientId]);

    // Fast client-side update when hierarchy/filters change (no refetch)
    React.useEffect(() => {
        if (!displayRawData.length) {
            setAllEntries([]);
            return;
        }
        setAllEntries(buildHierarchicalGroups(filteredRawData, hierarchy));
    }, [displayRawData, filteredRawData, hierarchy]);

    // Define KPI labels and their keys in TableData
    const kpiRows: { label: string, key: keyof TableData }[] = [
        { label: 'Sales Plan', key: 'salesPlan' },
        { label: 'Sales', key: 'sales' },
        { label: 'Sales Comp', key: 'salesComp' },
        { label: 'Markdowns Plan', key: 'markdownsPlan' },
        { label: 'Markdowns', key: 'markdowns' },
        { label: 'Markdowns Comp', key: 'markdownsComp' },
        { label: 'Markdown % Plan', key: 'mardownPctPlan' },
        { label: 'Markdown %', key: 'markdownPct' },
        { label: 'Markdown % Comp', key: 'markdownPctComp' },
        { label: 'IMU Plan', key: 'imuPlan' },
        { label: 'IMU', key: 'imu' },
        { label: 'IMU Comp', key: 'imuComp' },
        { label: 'MMU Plan', key: 'mmuPlan' },
        { label: 'MMU', key: 'mmu' },
        { label: 'MMU Comp', key: 'mmuComp' },
        { label: 'Cash Margin Plan', key: 'cashMarginPlan' },
        { label: 'Cash Margin', key: 'cashMargin' },
        { label: 'Cash Margin Comp', key: 'cashMarginComp' },
        { label: 'Cash Margin % Plan', key: 'cashMarginPctPlan' },
        { label: 'Cash Margin %', key: 'cashMarginPct' },
        { label: 'Cash Margin % Comp', key: 'cashMarginPctComp' },
        { label: 'Inflow Plan Cost', key: 'inflowPlanCost' },
        { label: 'Inflow Cost', key: 'inflowCost' },
        { label: 'Inflow Cost Comp', key: 'inflowCostComp' },
        { label: 'Inflow Plan Retail', key: 'inflowPlanRetail' },
        { label: 'Inflow Retail', key: 'inflowRetail' },
        { label: 'Inflow Retail Comp', key: 'inflowRetailComp' },
        { label: 'Cost of Purchases Plan %', key: 'costOfPurchasesPlanPct' },
        { label: 'Cost of Purchases %', key: 'costOfPurchasesPct' },
        { label: 'Cost of Purchases % Comp', key: 'costOfPurchasesPctComp' },
        { label: 'Turn Plan', key: 'turnPlan' },
        { label: 'Turn', key: 'turn' },
        { label: 'Turn Comp', key: 'turnComp' },
        { label: 'Efficiency Plan', key: 'efficiencyPlan' },
        { label: 'Efficiency', key: 'efficiency' },
        { label: 'Efficiency Comp', key: 'efficiencyComp' },
        { label: 'CMROI Plan', key: 'cmroiPlan' },
        { label: 'CMROI', key: 'cmroi' },
        { label: 'CMROI Comp', key: 'cmroiComp' },
        { label: 'GMROI Plan', key: 'gmroiPlan' },
        { label: 'GMROI', key: 'gmroi' },
        { label: 'GMROI Comp', key: 'gmroiComp' },
    ];

    // Map group key to TableData
    function getTableDataByGroupKey(key: string): TableData | undefined {
        return allEntries.find(e => e.key === key)?.data;
    }

    const [sortByKpi, setSortByKpi] = React.useState<keyof TableData | null>(null);
    const [sortByKpiDir, setSortByKpiDir] = React.useState<'asc' | 'desc'>('desc');

    const handleKpiSortClick = (key: keyof TableData) => {
        if (sortByKpi === key) {
            setSortByKpiDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortByKpi(key);
            setSortByKpiDir('desc');
        }
    };

    const parseKpiValue = (v: any): number => {
        if (v === null || v === undefined) return -Infinity;
        const s = String(v).replace(/[$,%\s]/g, '');
        const n = parseFloat(s);
        return isNaN(n) ? -Infinity : n;
    };

    const isEmptyCell = (v: any) => {
        if (v === null || v === undefined) return true;
        const s = String(v).trim();
        return s === '' || s === '-';
    };

    const visibleEntries = React.useMemo(() => {
        return allEntries.filter(e =>
            kpiRows.some(r => !isEmptyCell((e.data as any)[r.key]))
        );
    }, [allEntries, kpiRows]);

    const sortedEntries = React.useMemo(() => {
        if (!sortByKpi) return visibleEntries;

        const maxDepth = visibleEntries.reduce((max, e) => Math.max(max, e.depth), 0);

        if (maxDepth <= 1) {
            const companyEntries = visibleEntries.filter(e => e.depth === 0);
            const others = [...visibleEntries.filter(e => e.depth > 0)].sort((a, b) => {
                const va = parseKpiValue((a.data as any)[sortByKpi]);
                const vb = parseKpiValue((b.data as any)[sortByKpi]);
                return sortByKpiDir === 'asc' ? va - vb : vb - va;
            });
            return [...companyEntries, ...others];
        }

        // N-level hierarchical sort: company total first, then sort within each parent group recursively
        const companyEntries = visibleEntries.filter(e => e.depth === 0);
        const kpiKey = sortByKpi;

        function sortChildren(parentKey: string | undefined, depth: number): GroupEntry[] {
            const children = visibleEntries.filter(e => e.depth === depth && e.parentKey === parentKey);
            const sortedChildren = [...children].sort((a, b) => {
                const va = parseKpiValue((a.data as any)[kpiKey]);
                const vb = parseKpiValue((b.data as any)[kpiKey]);
                return sortByKpiDir === 'asc' ? va - vb : vb - va;
            });
            const result: GroupEntry[] = [];
            for (const child of sortedChildren) {
                result.push(child);
                if (depth < maxDepth) {
                    result.push(...sortChildren(child.key, depth + 1));
                }
            }
            return result;
        }

        return [...companyEntries, ...sortChildren(undefined, 1)];
    }, [visibleEntries, sortByKpi, sortByKpiDir, hierarchy]);

    const handleExportCsv = React.useCallback(() => {
        if (!sortedEntries.length) return;

        const fileName = `kpi_table_${clientId}_${startEpochMonth}-${endEpochMonth}`;

        if (!swapDimensions) {
            // KPI rows (same as UI default)
            const columns = ['KPI', ...sortedEntries.map(e => e.displayLabel)];
            const data: Array<Array<string | number>> = kpiRows.map(r => {
                return [
                    r.label,
                    ...sortedEntries.map(({ data: entryData }) => {
                        const v = (entryData as any)[r.key];
                        return (v === null || v === undefined || String(v).trim() === '') ? '-' : v;
                    })
                ];
            });

            ReportService.tableToCSV({ columns, data, fileName });
            return;
        }

        // KPI columns (transposed)
        const columns = ['Group', ...kpiRows.map(r => r.label)];
        const data: Array<Array<string | number>> = sortedEntries.map(({ displayLabel, data: entryData }) => {
            return [
                displayLabel,
                ...kpiRows.map(r => {
                    const v = (entryData as any)[r.key];
                    return (v === null || v === undefined || String(v).trim() === '') ? '-' : v;
                })
            ];
        });

        ReportService.tableToCSV({ columns, data, fileName });
    }, [
        sortedEntries,
        kpiRows,
        clientId,
        startEpochMonth,
        endEpochMonth,
        swapDimensions,
        locationFilter,
        classFilter,
        categoryFilter,
        compareToYearsAgo,
    ]);

    const handleExportPdf = React.useCallback(() => {
        if (!sortedEntries.length) return;

        // Build columns and data based on current orientation (same as UI)
        let fullColumns: string[] = [];
        let fullData: Array<Array<string | number>> = [];
        if (!swapDimensions) {
            // KPI in first column, groups as columns
            fullColumns = ['KPI', ...sortedEntries.map(e => e.displayLabel)];
            fullData = kpiRows.map(row => [
                row.label,
                ...sortedEntries.map(({ data: entryData }) => {
                    const v = (entryData as any)[row.key];
                    return (v === null || v === undefined || String(v).trim() === '') ? '-' : v;
                })
            ]);
        } else {
            // Groups in first column, KPIs as columns
            fullColumns = ['Group', ...kpiRows.map(r => r.label)];
            fullData = sortedEntries.map(({ displayLabel, data: entryData }) => [
                displayLabel,
                ...kpiRows.map(row => {
                    const v = (entryData as any)[row.key];
                    return (v === null || v === undefined || String(v).trim() === '') ? '-' : v;
                })
            ]);
        }

        // PDF meta
        const title = 'KPI Table';
        const hierarchyLabel = Object.entries(HIERARCHY_OPTIONS).find(([, v]) => JSON.stringify(v) === JSON.stringify(hierarchy))?.[0] ?? hierarchy.join(' > ');
        const subtitle = `Client ${clientId} | ${startEpochMonth} to ${endEpochMonth} | ${hierarchyLabel}`;

        // Create PDF and paginate horizontally by columns
        const orientation: 'l' | 'p' = 'l';
        const pageSize = 'a3';
        const doc = new jsPDF(orientation, 'pt', pageSize);

        const marginLeft = 20;
        const marginRight = 20;
        let startY = 40;

        doc.setFontSize(20);
        doc.text(title, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
        startY += 30;
        doc.setFontSize(14);
        doc.text(subtitle, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
        startY += 20;

        // How many data columns per page (not counting the first descriptor column)
        const columnsPerPage = 10; // adjust as needed
        const descriptor = fullColumns[0];
        const groupCols = fullColumns.slice(1);

        const totalSlices = Math.ceil(groupCols.length / columnsPerPage);
        for (let i = 0; i < totalSlices; i++) {
            if (i > 0) {
                doc.addPage(pageSize, orientation);
                startY = 40;
                doc.setFontSize(20);
                doc.text(title, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 30;
                doc.setFontSize(14);
                doc.text(`${subtitle} | Page ${i + 1} of ${totalSlices}`, doc.internal.pageSize.getWidth() / 2, startY, { align: 'center' });
                startY += 20;
            }

            const sliceCols = groupCols.slice(i * columnsPerPage, (i + 1) * columnsPerPage);
            const pageColumns = [descriptor, ...sliceCols];
            const pageData = fullData.map(r => [r[0], ...r.slice(1).slice(i * columnsPerPage, (i + 1) * columnsPerPage)]);

            autoTable(doc, {
                head: [pageColumns],
                body: pageData as any,
                startY,
                margin: { left: marginLeft, right: marginRight },
                styles: {
                    fontSize: 8,
                    cellPadding: 1,
                    overflow: 'linebreak',
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.5,
                    lineColor: [180, 180, 180],
                    cellWidth: 'wrap',
                },
                headStyles: {
                    fillColor: [51, 51, 51],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.5,
                    lineColor: [120, 120, 120],
                    fontSize: 8,
                },
                bodyStyles: {
                    halign: 'center',
                    valign: 'middle',
                    lineWidth: 0.5,
                    lineColor: [180, 180, 180],
                    fontSize: 8,
                },
                tableLineWidth: 0.5,
                tableLineColor: [120, 120, 120],
                columnStyles: {
                    ...Object.fromEntries(
                        pageColumns.map((_, index) => [index, { cellWidth: 'auto' }])
                    )
                },
                didDrawPage: (data) => {
                    const pageSizeObj = doc.internal.pageSize as any;
                    const pageHeight = pageSizeObj.height ? pageSizeObj.height : pageSizeObj.getHeight();
                    const pageWidth = pageSizeObj.width ? pageSizeObj.width : pageSizeObj.getWidth();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
                        data.settings.margin.left,
                        pageHeight - 10
                    );
                    const date = new Date().toLocaleDateString();
                    doc.text(
                        `Generated on: ${date}`,
                        pageWidth - 120,
                        pageHeight - 10
                    );
                }
            });
        }

        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
    }, [
        sortedEntries,
        kpiRows,
        clientId,
        startEpochMonth,
        endEpochMonth,
        swapDimensions,
        locationFilter,
        classFilter,
        categoryFilter,
        compareToYearsAgo,
    ]);

    return (
        <div className="p-4 border rounded-lg shadow-sm bg-white">
            <div className="flex items-center mb-4 gap-4">
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">Start Period</span>
                    <MonthSelector epochMonth={startEpochMonth} setEpochMonth={setStartEpochMonth} minMonth={endEpochMonth - 48} maxMonth={endEpochMonth} />
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">End Period</span>
                    <MonthSelector epochMonth={endEpochMonth} setEpochMonth={setEndEpochMonth} minMonth={EpochMonthService.getTodayEpochMonth() - 48} maxMonth={EpochMonthService.getTodayEpochMonth()} />
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">Hierarchy</span>
                    <select
                        className="border rounded px-2 py-1 text-sm"
                        value={Object.entries(HIERARCHY_OPTIONS).find(([, v]) => JSON.stringify(v) === JSON.stringify(hierarchy))?.[0] ?? 'By Location'}
                        onChange={e => setHierarchy(HIERARCHY_OPTIONS[e.target.value])}
                    >
                        {Object.keys(HIERARCHY_OPTIONS).map(key => (
                            <option key={key} value={key}>{key}</option>
                        ))}
                    </select>
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">Compare To</span>
                    <select
                        className="border rounded px-2 py-1 text-sm"
                        value={compareToYearsAgo}
                        onChange={e => setCompareToYearsAgo(parseInt(e.target.value))}
                    >
                        <option value={1}>1 Year Ago</option>
                        <option value={2}>2 Years Ago</option>
                        <option value={3}>3 Years Ago</option>
                    </select>
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">Orientation</span>
                    <select
                        className="border rounded px-2 py-1 text-sm"
                        value={swapDimensions ? 'kpi-columns' : 'kpi-rows'}
                        onChange={e => setSwapDimensions(e.target.value === 'kpi-columns')}
                    >
                        <option value="kpi-rows">KPI on left (default)</option>
                        <option value="kpi-columns">KPI on top</option>
                    </select>
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">Location Filter</span>
                    <MultiSelect
                        options={locationOptions}
                        value={locationFilter}
                        onChange={(v) => setLocationFilter((v && v.length) ? v : ['ALL'])}
                        placeholder="All Locations"
                    />
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">Category Filter</span>
                    <MultiSelect
                        options={categoryOptions}
                        value={categoryFilter}
                        onChange={(v) => setCategoryFilter((v && v.length) ? v : ['ALL'])}
                        placeholder="All Categories"
                    />
                </div>
                <div className="flex flex-col items-start">
                    <span className="text-xs font-semibold text-slate-600">Class Filter</span>
                    <MultiSelect
                        options={classOptions}
                        value={classFilter}
                        onChange={(v) => setClassFilter((v && v.length) ? v : ['ALL'])}
                        placeholder="All Classes"
                    />
                </div>
                <div className="ml-auto flex items-end gap-2">
                    <button onClick={handleExportPdf} className="px-3 py-1 border rounded text-sm bg-slate-50 hover:bg-slate-100">Export PDF</button>
                    <button onClick={handleExportCsv} className="px-3 py-1 border rounded text-sm bg-slate-50 hover:bg-slate-100">Export CSV</button>
                </div>
            </div>
            {loading ? (
                <div className="text-center py-4">Loading...</div>
            ) : error ? (
                <div className="text-center py-4 text-red-500">{error}</div>
            ) : (
                <div className="overflow-x-auto overflow-y-auto max-h-[500px] max-w-[1700px] border rounded">
                    {!swapDimensions ? (
                        // Original layout: KPI as rows, groups as columns
                        <table className="min-w-max w-full text-xs">
                            <thead>
                                <tr>
                                    <th className="sticky left-0 bg-white z-10 border-b font-bold text-left px-2 py-1">KPI</th>
                                    {sortedEntries.map(({ key, depth, displayLabel }) => {
                                        const thClass = hierarchy.length >= 2 && depth === 0
                                            ? 'border-b font-bold px-2 py-1 text-center whitespace-nowrap bg-tw-primary text-white'
                                            : hierarchy.length >= 2 && depth === 1
                                            ? 'border-b font-bold px-2 py-1 text-center whitespace-nowrap bg-tw-primary-medium-light text-white'
                                            : hierarchy.length >= 3 && depth === 2
                                            ? 'border-b font-bold px-2 py-1 text-center whitespace-nowrap bg-tw-primary-light text-gray-800'
                                            : 'border-b font-bold px-2 py-1 text-center whitespace-nowrap';
                                        return <th key={key} className={thClass}>{displayLabel}</th>;
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {kpiRows.map(row => (
                                    <tr key={String(row.key)} className="hover:bg-slate-100 transition-colors">
                                        <td
                                            className="sticky left-0 bg-white z-10 border-r font-semibold px-2 py-1 whitespace-nowrap cursor-pointer select-none hover:bg-slate-100"
                                            onClick={() => handleKpiSortClick(row.key)}
                                        >
                                            <span className="flex items-center gap-1">
                                                {row.label}
                                                {sortByKpi === row.key ? (sortByKpiDir === 'asc' ? ' ▲' : ' ▼') : <span className="text-gray-300"> ⇅</span>}
                                            </span>
                                        </td>
                                        {sortedEntries.map(({ key, depth, data: entryData }) => {
                                            const cellClass = hierarchy.length >= 2 && depth === 0
                                                ? 'px-2 py-1 text-center border-r bg-[#ede8f1] font-bold'
                                                : hierarchy.length >= 2 && depth === 1
                                                ? 'px-2 py-1 text-center border-r bg-[#f5f1f8] font-semibold'
                                                : hierarchy.length >= 3 && depth === 2
                                                ? 'px-2 py-1 text-center border-r bg-[#f9f7fc] font-medium'
                                                : 'px-2 py-1 text-center border-r';
                                            const val = (entryData as any)[row.key];
                                            return <td key={key} className={cellClass}>{val || '-'}</td>;
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        // Transposed layout: groups as rows, KPI as columns
                        <table className="min-w-max w-full text-xs">
                            <thead>
                                <tr>
                                    <th className="sticky left-0 bg-white z-10 border-b font-bold text-left px-2 py-1">Group</th>
                                    {kpiRows.map(row => (
                                        <th
                                            key={String(row.key)}
                                            className="border-b font-bold px-2 py-1 text-center whitespace-nowrap cursor-pointer select-none hover:bg-slate-100"
                                            onClick={() => handleKpiSortClick(row.key)}
                                        >
                                            {row.label}{sortByKpi === row.key ? (sortByKpiDir === 'asc' ? ' ▲' : ' ▼') : <span className="text-gray-300"> ⇅</span>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedEntries.map(({ key, depth, displayLabel, data: entryData }) => {
                                    const isCompanyTotal = depth === 0 && hierarchy.length >= 2;
                                    const isSubtotal = depth === 1 && hierarchy.length >= 2;
                                    const isSubSubtotal = depth === 2 && hierarchy.length >= 3;
                                    const rowClass = isCompanyTotal
                                        ? 'font-bold bg-tw-primary text-white'
                                        : isSubtotal
                                        ? 'font-bold bg-tw-primary-medium-light text-white'
                                        : isSubSubtotal
                                        ? 'font-semibold bg-tw-primary-light text-gray-800'
                                        : 'hover:bg-slate-100 transition-colors';
                                    const stickyBg = isCompanyTotal
                                        ? 'bg-tw-primary'
                                        : isSubtotal
                                        ? 'bg-tw-primary-medium-light'
                                        : isSubSubtotal
                                        ? 'bg-tw-primary-light'
                                        : 'bg-white';
                                    const indentClass = depth >= 3 && hierarchy.length >= 3 ? 'pl-10' : depth >= 2 && hierarchy.length >= 2 ? 'pl-6' : 'pl-2';
                                    return (
                                        <tr key={key} className={rowClass}>
                                            <td className={`sticky left-0 ${stickyBg} z-10 border-r font-semibold py-1 pr-2 ${indentClass} whitespace-nowrap`}>{displayLabel}</td>
                                            {kpiRows.map(row => {
                                                const val = (entryData as any)[row.key];
                                                return (
                                                    <td key={String(row.key)} className="px-2 py-1 text-center border-r">{val || '-'}</td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    )
}
